import { Compilation } from '../interfaces/compilations.interfaces';
import { transformQuestFromServer } from './quest.functions';
import { transformObeliskFromServer } from './exploration.functions';
import { Dict } from '../interfaces/common.interfaces';

/**
 * Transforms a compilation object from server (snake_case) to client (camelCase) format
 * @param data Raw compilation data from server
 * @param apiUrl API URL for transforming nested quests
 * @returns Transformed Compilation object
 */
export function transformCompilationFromServer(data: Dict<any>, apiUrl: string): Compilation {
  const questsCount = data['quests_count'] ?? 0;
  const completedQuestsCount = data['count_completed_quests'] ?? 0;
  const obelisksCount = data['obelisks_count'] ?? 0;
  const visitedObelisksCount = data['count_visited_obelisks'] ?? 0;

  const totalEntitiesCount = questsCount + obelisksCount;

  const progress = Math.round((totalEntitiesCount === 0 ? 0 : (completedQuestsCount + visitedObelisksCount) / totalEntitiesCount) * 100);

  // Transform quests and obelisks first so we can resolve cluster references
  const transformedQuests = data['quests']?.map((quest: any) => transformQuestFromServer(quest, apiUrl)) ?? [];
  const transformedObelisks = data['obelisks']?.map((obelisk: any) => transformObeliskFromServer(obelisk)) ?? [];

  // Build lookup maps for resolving lightweight cluster references produced by the copy endpoint
  // (each copied cluster item is a stub like { id, type: 'quest' | 'obelisk' })
  const questById = new Map<number, any>();
  const obeliskByUuid = new Map<string, any>();

  transformedQuests.forEach((q: any) => { if (q.id != null) questById.set(Number(q.id), q); });
  transformedObelisks.forEach((o: any) => { if (o.uuid) obeliskByUuid.set(String(o.uuid), o); });

  const tripConfig = data['trip_config'] ?? {};

  // Resolve cluster stubs inside each location.
  // Real ObeliskType is 'stationary', real QuestMetadata has 'steps' — so
  // type==='quest' or type==='obelisk' unambiguously marks a lightweight stub.
  const resolvedLocations =
    data['is_copy'] ? (tripConfig.locations?.map((location: any) => ({
      ...location,
      clusters: (location.clusters ?? []).map((cluster: any) => {
        const item = cluster[0];
        if (item?.type === 'quest' && item.id != null) {
          return questById.get(Number(item.id)) ?? item;
        }
        if (item?.type === 'obelisk' && item.id != null) {
          return obeliskByUuid.get(String(item.id)) ?? item;
        }
        return item;
      })
    })) ?? tripConfig.locations) : tripConfig.locations;

  return {
    id: data['id'],
    name: data['name'],
    description: data['description'],
    isPublic: data['is_public'],
    onlyForEvents: data['only_for_events'],
    type: data['type'],
    questsCount,
    completedQuestsCount,
    obelisksCount,
    visitedObelisksCount,
    progress,
    quests: transformedQuests,
    obelisks: transformedObelisks,
    creator: {
      id: data['creator'],
      username: data['creator_username']
    },
    location: data['geom'] ? {
      lng: data['geom']['coordinates'][1],
      lat: data['geom']['coordinates'][0]
    } : undefined,
    tripConfig: {
      ...tripConfig,
      titleImageUrl: tripConfig['title_image_url'],
      locations: resolvedLocations
    },
    activityTags: data['activity_tags'] ?? [],
    isFavorite: data['is_favorite'],
    isCopy: data['is_copy'] ?? false,
    originalTrip: data['original_trip'] ?? null
  };
}

